<template>
  <v-container class="general-info-client px-6" fluid>
      <v-row align="center" class="general-info-client__header">
        <div class="general-info-client__title-block">
          <v-card-title class="general-info-client__title">
            <v-icon left color="primary">mdi-account-outline</v-icon>
            Клиент
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  text
                  small
                  class="ml-2"
                  v-bind="attrs"
                  v-on="on"
                  :loading="refreshingClient"
                  @click="refreshClientData"
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Обновить данные клиента</span>
            </v-tooltip>
          </v-card-title>
          <p v-if="client.without_doorphone" class="general-info-client__without-doorphone-note">
            Клиент создан через ЛК, без домофона
          </p>
        </div>
        <v-spacer />
        <v-btn
          v-if="isRoleNameAdmin"
          color="primary"
          class="text-none"
          rounded
          @click.stop="openEditModal"
        >
          <v-icon left small>mdi-pencil-outline</v-icon>
          Редактировать
        </v-btn>
        <v-dialog v-model="editDialog[client.id]" max-width="500" content-class="dialog-border-radius">
          <EditClientModal
            :active.sync="editDialog[client.id]"
            @close="$set(editDialog, client.id, false)"
            :id="client.id"
            message="Редактирование клиента"
            :initial-without-doorphone="editInitialWithoutDoorphone"
            :show-without-doorphone-option="!(client.abonents && client.abonents.length)"
            :attPopupEditContent="editFormContent || {}"
            @submit="onEditClientSubmit"/>
        </v-dialog>
      </v-row>
      <v-card-text class="general-info-client__info pt-2 pb-4">
        <div class="info-list">
          <div v-if="client.id" class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-identifier</v-icon>
            <span class="info-list__label">ID</span>
            <span class="info-list__value">{{ client.id }}</span>
          </div>
          <div v-if="client.name" class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-account-outline</v-icon>
            <span class="info-list__label">Имя</span>
            <span class="info-list__value">{{ client.name }}</span>
          </div>
          <div v-if="client.email" class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-email-outline</v-icon>
            <span class="info-list__label">Email</span>
            <span class="info-list__value">{{ client.email }}</span>
          </div>
          <div v-if="client.telephone" class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-phone-outline</v-icon>
            <span class="info-list__label">Телефон</span>
            <span class="info-list__value">{{ formatPhoneNumber(client.telephone) }}</span>
          </div>
          <div v-if="clientCarNumbersWithStatus.length" class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-car-side</v-icon>
            <span class="info-list__label">Номер автомобиля</span>
            <span class="info-list__value">
              <span v-for="(item, index) in clientCarNumbersWithStatus" :key="index">
                {{ item.number }}<span v-if="item.isActive !== undefined" :class="item.isActive ? ' success--text' : ' grey--text'">{{ item.isActive ? ' — активный' : ' — неактивный' }}</span>
                <v-tooltip v-if="isRoleNameAdmin && item.isActive === false && item.carNumberId && clientBarrierIdsForActivate.length" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      text
                      x-small
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                      disabled
                      :loading="activatingCarNumberId === item.carNumberId"
                      @click.stop="activateCarNumberForClient(item)"
                    >
                      <v-icon x-small>mdi-check-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Активировать гос. номер</span>
                </v-tooltip>
                {{ index !== clientCarNumbersWithStatus.length - 1 ? ', ' : '' }}
              </span>
            </span>
          </div>
          <div v-if="client.comment" class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-comment-text-outline</v-icon>
            <span class="info-list__label">Комментарий</span>
            <span class="info-list__value">{{ client.comment }}</span>
          </div>
          <div class="info-list__row">
            <v-icon small class="info-list__icon grey--text">mdi-domain</v-icon>
            <span class="info-list__label">ЖК</span>
            <span class="info-list__value">{{ uniqueResidentialComplexes.join(', ') || '—' }}</span>
          </div>
        </div>
      </v-card-text>
      <v-card flat>
        <v-col cols="12" class="general-info-client__actions" :class="{ 'mb-12': isRoleNameAdmin }">
          <v-btn
            v-if="isRoleNameAdmin && !initialWithoutDoorphoneForEdit"
            color="primary"
            class="text-none"
            rounded
            @click.prevent="newAbonentClient(client.id)"
          >
            <v-icon left small>mdi-account-plus-outline</v-icon>
            Добавить абонента
          </v-btn>
          <v-btn
            v-if="isRoleNameAdmin && showCreateAccountButton"
            :loading="creatingAccounts"
            :disabled="creatingAccounts"
            color="primary"
            class="text-none"
            rounded
            @click.prevent="createMissingAccounts"
          >
            <v-icon left small>mdi-file-document-outline</v-icon>
            Создать лицевой счёт
          </v-btn>
        </v-col>
        <v-dialog v-model="errorsDialog" max-width="600" content-class="dialog-border-radius">
          <v-card>
            <v-card-title class="headline">Ошибки создания ЛС</v-card-title>
            <v-card-text>
              <div v-if="Array.isArray(accountCreationErrors) && accountCreationErrors.length">
                <div v-for="(err, idx) in accountCreationErrors" :key="idx" class="mb-2">
                  {{ String(err) }}
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary darken-1" text @click="errorsDialog = false">Закрыть</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-spacer />
        <v-row align="center" class="section-header">
          <v-card-title v-if="client" class="section-title mb-2">
            <v-icon left color="primary">mdi-doorbell-video</v-icon>
            Домофоны
          </v-card-title>
          <div class="mt-4" v-if="displayLoading">
            <v-progress-circular color="primary" indeterminate />
          </div>
        </v-row>
        <div v-if="client && (!client.abonents || !client.abonents.length)" class="grey--text mb-6">
          Клиент без домофона
        </div>
        <div v-if="displayCardTitle || displayTable" class="general-info-client__doorphone-card mb-6">
          <v-tabs
            v-if="displayCardTitle"
            v-model="activeDoorphoneTab"
            class="general-info-client__doorphone-tabs"
            show-arrows="mobile"
          >
            <v-tab>Основные</v-tab>
            <v-tab>Калитки</v-tab>
            <v-tab>Доп. входы</v-tab>
          </v-tabs>
          <v-data-table
            class="table"
            :headers="headers"
            :items="filteredTableAbonents"
            v-if="displayTable"
            :items-per-page="10"
            :disable-sort="true"
            :footer-props="{
              'items-per-page-options': [10, 20],
              }"
          >
          <template v-slot:item.id="{ item }">
            <router-link
              v-if="getAbonentDoorphoneForTab(item)"
              class="routerLink"
              :to="`/doorphones/${getAbonentDoorphoneForTab(item).id}`"
            >
              {{ getAbonentDoorphoneForTab(item).id }}
            </router-link>
            <span v-else>—</span>
          </template>
          <template v-slot:item.full_address="{ item }">
            <router-link class="routerLink" :to="`${item.doorphone_router_link}`">
              {{ item.full_address }}
            </router-link>
            <div
              v-if="item.activation_expires_at"
              class="temporary-note"
              style="color: #0d6efd; margin-top: 4px;"
            >
              Активен до {{ ruDateFormat(item.activation_expires_at, true) }}
            </div>
            <div
              v-if="item.is_domyland"
              class="domyland-note"
              style="color: red; margin-top: 4px;"
            >
              Клиент Домиленда
            </div>
          </template>
          <template v-slot:item.telephone="{ item }">
            <router-link class="routerLink" :to="`/resident-accounts/${item.id}`">
              {{ formatPhoneNumber(item.telephone) }}
            </router-link>
          </template>
          <template v-slot:item.status="{ item }">
            <span :class="'colorStatus' + item.status">{{ item.status_text }}</span>
          </template>
          <template v-slot:item.forbid_car_number_activation="{ item }">
            <div v-if="isRoleNameAdmin && hasMainDoorphone(item)">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    text
                    tile
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="btn__icon pa-0"
                    width="18"
                    height="18"
                    style="cursor: default;"
                    @click="$set(dialogForbidCarNumber, item.id, true)"
                    :aria-disabled="item.status === 'D'"
                  >
                    <v-icon :color="item.forbid_car_number_activation ? 'red' : 'green'" x-small>mdi-checkbox-blank-circle</v-icon>
                  </v-btn>
                </template>
                <span v-if="item.forbid_car_number_activation">Запрещена активация ГРЗ в моб. приложении</span>
                <span v-else>Разрешена активация ГРЗ в моб. приложении</span>
              </v-tooltip>
              <v-dialog
                v-model="dialogForbidCarNumber[item.id]"
                persistent
                :retain-focus="false"
                max-width="400"
                content-class="dialog-border-radius"
                :key="'car-' + item.id"
              >
                <v-card class="apartment-modal-card">
                  <v-card-title class="apartment-modal-title">
                    <span class="apartment-modal-title-inner">
                      <v-icon :color="item.forbid_car_number_activation ? 'success' : 'orange'" class="apartment-modal-title-icon">mdi-car-cog</v-icon>
                      <span v-if="item.forbid_car_number_activation">Разрешить активацию ГРЗ в моб. приложении?</span>
                      <span v-else>Запретить активацию ГРЗ в моб. приложении?</span>
                    </span>
                  </v-card-title>
                  <v-card-text class="apartment-modal-text">
                    Номер телефона: <strong>{{ item.telephone }}</strong>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions class="apartment-modal-actions">
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      outlined
                      rounded
                      @click="$set(dialogForbidCarNumber, item.id, false)"
                    >
                      Назад
                    </v-btn>
                    <v-btn
                      v-if="item.forbid_car_number_activation"
                      color="success"
                      rounded
                      @click.prevent="allowCarNumberActivationAbonent(item.id); $set(dialogForbidCarNumber, item.id, false)"
                    >
                      Разрешить
                    </v-btn>
                    <v-btn
                      v-else
                      color="orange darken-2"
                      rounded
                      @click.prevent="forbidCarNumberActivationAbonent(item.id); $set(dialogForbidCarNumber, item.id, false)"
                    >
                      Запретить
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
            <div v-else-if="hasMainDoorphone(item)">
              <span v-if="item.forbid_car_number_activation" style="color: red;">Запрещено</span>
              <span v-else style="color: green;">Разрешено</span>
            </div>
            <span v-else>—</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="$checkPermission('destroy_abonent') && hasMainDoorphone(item)"
                  icon
                  text
                  tile
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="btn__icon pa-0"
                  width="18"
                  height="18"
                  @click.stop="$set(dialog, item.id, true)"
                >
                  <v-icon size="large">mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Удалить абонента</span>
            </v-tooltip>
            <v-dialog
              v-model="dialog[item.id]"
              persistent
              :retain-focus="false"
              max-width="350"
              :key="item.id"
            >
              <v-card>
                <v-card-title class="headline">Удалить аккаунт?</v-card-title>
                <v-card-text>Подтверждая, вы удалите аккаунт.</v-card-text>
                <v-text-field
                  class="ma-3"
                  label="Комментарий"
                  v-model='commentDelete'
                  type="string"
                  clearable
                  dense>
                </v-text-field>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary darken-1"
                    text
                    @click="$set(dialog, item.id, false)"
                  >
                    Отменить
                  </v-btn>
                  <v-btn
                    color="primary darken-1"
                    text
                    @click.prevent="removeAbonent(item.id)"
                  >
                    Удалить
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          </v-data-table>
        </div>
        <v-row align="center" class="section-header">
          <v-card-title v-if="client" class="section-title mb-2">
            <v-icon left color="primary">mdi-gate</v-icon>
            Шлагбаумы
          </v-card-title>
          <div class="mt-4" v-if="displayLoadingBarriers">
            <v-progress-circular color="primary" indeterminate />
          </div>
        </v-row>
        <v-data-table
          class="table mb-6"
          :headers="headersBarriers"
          :items="uniqueBarriers"
          v-if="displayBarriersTable"
          :items-per-page="10"
          :disable-sort="true"
          :footer-props="{
            'items-per-page-options': [10, 20],
            }"
        >
          <template v-slot:item.installed_at="{ item }">
            {{ ruDateFormat(item.barrier.installed_at, true) }}
          </template>
          <template v-slot:item.car_number="{ item }">
            <div v-for="(number, index) in item.abonent.car_number" :key="index">
              {{ number }}
            </div>
          </template>
          <template v-slot:item.address="{ item }">
            <router-link class="routerLink" :to="`/barrier/${item.barrier.id}`">
              {{ item.barrier.address }}
            </router-link>
          </template>
          <template v-slot:item.full_address="{ item }">
            <router-link v-if="item.abonent && item.abonent.id" class="routerLink" :to="`${item.abonent.doorphone_router_link}`">
              {{ item.abonent.full_address }}
            </router-link>
            <span v-else>{{ item.abonent ? item.abonent.full_address : '—' }}</span>
          </template>
          <template v-slot:item.telephone="{ item }">
            <router-link v-if="item.abonent && item.abonent.id" class="routerLink" :to="`/resident-accounts/${item.abonent.id}`">
              {{ item.abonent.telephone ? formatPhoneNumber(item.abonent.telephone) : '—' }}
            </router-link>
            <span v-else-if="item.abonent && item.abonent.telephone">{{ formatPhoneNumber(item.abonent.telephone) }}</span>
            <span v-else>—</span>
          </template>
          <template v-slot:item.history_payment>
            Просмотр
          </template>
          <template v-slot:item.sipStatus="{ item }">
            <div>
              <v-icon x-small :color="getBarrierSipStatusColor(item.barrier)">
                mdi-checkbox-blank-circle
              </v-icon>
              {{ getBarrierSipStatusText(item.barrier) }}
            </div>
          </template>
          <template v-slot:item.action="{ item }">
            <v-row class="flex-nowrap" justify="center">
              <v-tooltip bottom v-if="item.abonent && item.abonent.id">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="$checkPermission('simple_update_abonent')"
                    icon
                    text
                    tile
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="btn__icon pa-0"
                    width="18"
                    height="18"
                    @click.stop="$set(dialogEditAbonet, item.abonent.id, true)"
                  >
                    <v-icon>mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-dialog v-model="dialogEditAbonet[item.abonent.id]" max-width="800" content-class="dialog-border-radius">
                    <BaseEditAbonent
                      :active.sync="dialogEditAbonet[item.abonent.id]"
                      @close="$set(dialogEditAbonet, item.abonent.id, false)"
                      :id="item.abonent.id"
                      :response-cars="item.barrier.car_numbers"
                      message="Редактирование абонента"
                      :attPopupEditContent="JSON.parse(JSON.stringify(item.abonent))"
                      @submit="reloadPage"
                      icon="mdi-pencil-outline"
                    />
                  </v-dialog>
                </template>
                <span>Редактировать</span>
              </v-tooltip>
              <span v-else>—</span>
            </v-row>
          </template>
        </v-data-table>
        <v-card-actions class="general-info-client__footer mb-4 mt-6">
          <v-spacer />
          <v-btn
            v-if="isRoleNameAdmin"
            color="error"
            outlined
            rounded
            class="text-none"
            @click.stop="$set(removeDialog, client.id, true)"
          >
            <v-icon left small>mdi-account-remove-outline</v-icon>
            Удалить клиента
          </v-btn>
          <v-dialog v-model="removeDialog[client.id]" width="500">
            <BaseDeleteModal
              :active.sync="removeDialog[client.id]"
              @close="$set(removeDialog, client.id, false)"
              @submit="removeClient(client.id)"
              messageTitle="Удалить клиента?"
              messageText="Подтверждая, вы удалите клиента."/>
          </v-dialog>
        </v-card-actions>
      </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EditClientModal from '@/components/client/DetailClient/components/EditClientModal';
import BaseDeleteModal from '@/components/BaseDeleteModal';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import BaseEditAbonent from '../../../BaseEditAbonent';
import { getBarrierSipStatusColor, getBarrierSipStatusText } from '@/components/barrier/consts';

export default {
  name: 'GeneralInfoClient',
  mixins: [ruDateFormatMixin],
  components: { BaseDeleteModal, EditClientModal, BaseEditAbonent },
  props: ['id'],
  data () {
    return {
      token: '',
      include: ['abonents.doorphones', 'abonents.keys', 'abonents.apartment_sip_settings', 'barriers'],
      tableKey: 0,
      abonentsLoaded: false,
      isRoleNameAdmin: false,
      creatingAccounts: false,
      billingInfoErrorShown: false,
      errorsDialog: false,
      accountCreationErrors: [],
      dialog: {},
      dialogForbidCarNumber: {},
      editDialog: {},
      dialogEditAbonet: {},
      removeDialog: {},
      editFormContent: null,
      editInitialWithoutDoorphone: false,
      refreshingClient: false,
      activatingCarNumberId: null,
      activeDoorphoneTab: 0,
      tableAbonents: [],
      resComplexsName: [],
      uniqueBarriers: [],
      commentDelete: '',
      headersBarriers: [
        { text: 'id', value: 'barrier.id' },
        { text: 'Адрес шлагбаума', value: 'address' },
        { text: 'Адрес абонента', value: 'full_address' },
        { text: 'Название', value: 'barrier.name' },
        { text: 'Телефон', value: 'telephone' },
        { text: 'Номер автомобиля', value: 'car_number' },
        { text: 'Статус', value: 'sipStatus' },
        { text: 'Дата установки', value: 'installed_at' },
        { text: '', value: 'action' },
      ],
      headers: [
        { text: 'ID', value: 'id', width: '8%' },
        { text: 'Адрес', value: 'full_address' },
        { text: 'ЛС домофона', value: 'account_id' },
        { text: 'Абонент', value: 'telephone' },
        { text: 'Ключ', value: 'key_code' },
        { text: 'Статус оплаты', value: 'apartment_paid' },
        { text: 'Баланс', value: 'money' },
        { text: 'Дата платежа', value: 'reckoning_date' },
        { text: 'Тариф', value: 'tp_name' },
        { text: 'Статус', value: 'status' },
        { text: 'ГРЗ', value: 'forbid_car_number_activation', sortable: false, width: '5%' },
        { text: '', value: 'actions' },
      ],
    };
  },
  created () {
    this.getData();
    switch (this.roleName) {
      case 'admin' : this.isRoleNameAdmin = true;
        break;
    }
    this.getResidentialComplex()
      .then((data) => {
        this.resComplexsName = data.data;
      })
      .catch((error) => {
        console.error('Ошибка при получении данных:', error);
      });
  },
  computed: {
    ...mapGetters({
      client: 'Client/client',
      roleName: 'roleName',
    }),
    uniqueResidentialComplexes () {
      if (!this.client || !Array.isArray(this.client.abonents)) {
        return [];
      }
      const complexes = new Set();

      this.client.abonents.forEach(abonent => {
        abonent.doorphones.forEach(doorphone => {
          const complex = this.resComplexsName.find(item => item.id === doorphone.residential_complex_id);
          if (complex) {
            complexes.add(complex.name);
          }
        });
      });

      return [...complexes];
    },
    displayCardTitle () {
      return Array.isArray(this.tableAbonents) && this.tableAbonents.length > 0;
    },
    displayLoading () {
      return Array.isArray(this.tableAbonents) && this.tableAbonents.length > 0 && !this.abonentsLoaded;
    },
    displayTable () {
      return Array.isArray(this.tableAbonents) && this.tableAbonents.length > 0 && this.abonentsLoaded;
    },
    filteredTableAbonents () {
      if (!Array.isArray(this.tableAbonents)) return [];
      if (this.activeDoorphoneTab === 1) {
        return this.tableAbonents.filter(item => this.getAbonentDoorphoneType(item) === 'G');
      }
      if (this.activeDoorphoneTab === 2) {
        return this.tableAbonents.filter(item => this.getAbonentDoorphoneType(item) === 'A');
      }
      return this.tableAbonents.filter(item => {
        const type = this.getAbonentDoorphoneType(item);
        return type !== 'G' && type !== 'A';
      });
    },
    displayLoadingBarriers () {
      return Array.isArray(this.tableAbonents) && this.tableAbonents.length > 0 && !this.abonentsLoaded;
    },
    displayBarriersTable () {
      return this.client && this.uniqueBarriers.length > 0;
    },
    initialWithoutDoorphoneForEdit () {
      if (!this.client) return false;
      if (this.client.without_doorphone === true) return true;
      const noAbonents = !this.client.abonents || this.client.abonents.length === 0;
      const hasBarriers = this.client.barriers && Array.isArray(this.client.barriers) && this.client.barriers.length > 0;
      return !!(noAbonents && hasBarriers);
    },
    clientCarNumbersDisplay () {
      const items = this.clientCarNumbersWithStatus;
      return items.length ? items.map(i => i.number).join(', ') : '';
    },
    clientCarNumbersWithStatus () {
      const arr = this.client && this.client.car_numbers;
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map(item => {
          const number = item && typeof item === 'object' && item.car_number != null ? item.car_number : item;
          if (number == null || number === '') return null;
          const isActive = item && typeof item === 'object' && 'is_active' in item ? item.is_active : undefined;
          const carNumberId = item && typeof item === 'object' && item.id != null ? item.id : null;
          return { number: String(number), isActive, carNumberId };
        }).filter(Boolean);
      }
      if (this.client && this.client.car_number != null && this.client.car_number !== '') {
        const raw = this.client.car_number;
        const number = typeof raw === 'string' ? raw : (raw && raw.car_number);
        const isActive = raw && typeof raw === 'object' && 'is_active' in raw ? raw.is_active : undefined;
        const carNumberId = raw && typeof raw === 'object' && raw.id != null ? raw.id : null;
        return number ? [{ number: String(number), isActive, carNumberId }] : [];
      }
      return [];
    },
    /** ID шлагбаумов клиента для активации гос. номера */
    clientBarrierIdsForActivate () {
      if (!this.client) return [];
      if (this.client.barriers && Array.isArray(this.client.barriers)) {
        return this.client.barriers.map(b => b && b.id != null ? b.id : b).filter(id => id != null);
      }
      return (this.uniqueBarriers || []).map(u => u.barrier && u.barrier.id).filter(id => id != null);
    },
    showCreateAccountButton () {
      if (!this.abonentsLoaded) {
        return false;
      }
      if (!Array.isArray(this.tableAbonents)) {
        return false;
      }
      return this.tableAbonents.some(a => {
        const hasMainDoorphone = Array.isArray(a.doorphones) && a.doorphones.some(d => d && d.type === 'M');
        if (!hasMainDoorphone) {
          return false;
        }
        const existingAccountId = a.account_id || (Array.isArray(a.apartment_sip_settings) && a.apartment_sip_settings[0] && a.apartment_sip_settings[0].account_id);
        return !existingAccountId;
      });
    },
  },
  methods: {
    getBarrierSipStatusColor,
    getBarrierSipStatusText,
    ...mapActions({
      loadClient: 'Client/loadClient',
      activateCarNumber: 'Client/activateCarNumber',
      infoClientPaid: 'Client/infoClientPaid',
      deleteClient: 'Client/deleteClient',
      connectEvoApi: 'Apartment/connectEvoApi',
      billingInfo: 'Apartment/billingInfo',
      createBillingAccount: 'Apartment/createBillingAccount',
      deleteAbonent: 'Abonents/deleteAbonent',
      forbidCarNumberActivation: 'Abonents/forbidCarNumberActivation',
      allowCarNumberActivation: 'Abonents/allowCarNumberActivation',
      getResidentialComplex: 'ResidentComplex/getComplex',
    }),
    getEditClientContent () {
      if (!this.client || !this.client.id) return {};
      const clone = JSON.parse(JSON.stringify(this.client));
      clone.without_doorphone = !!this.client.without_doorphone;
      if (!Array.isArray(clone.barrier_ids) && clone.barriers && Array.isArray(clone.barriers)) {
        clone.barrier_ids = clone.barriers.map(b => (b && b.id != null ? b.id : b)).filter(id => id != null);
      }
      return clone;
    },
    openEditModal () {
      this.editFormContent = this.getEditClientContent();
      this.editInitialWithoutDoorphone = this.initialWithoutDoorphoneForEdit;
      this.$set(this.editDialog, this.client.id, true);
    },
    onEditClientSubmit () {
      this.$set(this.editDialog, this.client.id, false);
      this.getData();
    },
    activateCarNumberForClient (item) {
      if (!item.carNumberId || !this.clientBarrierIdsForActivate.length) return;
      this.activatingCarNumberId = item.carNumberId;
      this.activateCarNumber({
        clientId: this.client.id,
        car_number_id: item.carNumberId,
        barrier_ids: this.clientBarrierIdsForActivate,
      })
        .then(() => {
          this.$flash_success('Гос. номер активирован');
          return this.getData();
        })
        .catch(() => {
          this.$flash_warning('Не удалось активировать гос. номер');
        })
        .finally(() => {
          this.activatingCarNumberId = null;
        });
    },
    refreshClientData () {
      this.refreshingClient = true;
      this.getData()
        .then(() => {
          this.$flash_success('Данные обновлены');
        })
        .catch(() => {
          this.$flash_warning('Не удалось обновить данные');
        })
        .finally(() => {
          this.refreshingClient = false;
        });
    },
    getData () {
      this.abonentsLoaded = false;
      return this.loadClient({
        id: this.$route.params.id,
        options: {
          include: this.include,
        },
      })
        .then(async () => {
          this.tableAbonents = Array.isArray(this.client && this.client.abonents)
            ? JSON.parse(JSON.stringify(this.client.abonents))
            : [];
          await this.getInfoEvoApi();
        });
    },
    formatPhoneNumber (phoneNumber) {
      phoneNumber = phoneNumber.toString();
      const digits = phoneNumber.replace(/\D/g, '');
      return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
    },
    reloadPage () {
      window.location.reload();
    },
    getBarriers () {
      this.uniqueBarriers = [];
      const abonents = this.client.abonents;

      if (abonents && abonents.length > 0) {
        abonents.forEach(abonent => {
          (abonent.doorphones || []).forEach(doorphone => {
            if (doorphone.barriers && doorphone.barriers.length > 0) {
              doorphone.barriers.forEach(barrier => {
                this.uniqueBarriers.push({
                  abonent: abonent,
                  barrier: barrier,
                });
              });
            }
          });
        });
      } else if (this.client.barriers && Array.isArray(this.client.barriers) && this.client.barriers.length > 0) {
        const carNumbers = this.client.car_numbers || (this.client.car_number ? [this.client.car_number] : []);
        const carNumber = Array.isArray(carNumbers)
          ? carNumbers.map(item => (item && typeof item === 'object' && item.car_number != null ? item.car_number : item)).filter(Boolean)
          : [];
        this.uniqueBarriers = this.client.barriers.map(barrier => ({
          barrier,
          abonent: {
            id: null,
            full_address: '—',
            doorphone_router_link: '#',
            telephone: this.client.telephone,
            car_number: carNumber,
          },
        }));
      }
    },
    getInfoEvoApi () {
      this.getBarriers();
      this.billingInfoErrorShown = false;
      if (!this.tableAbonents || this.tableAbonents.length === 0) {
        this.abonentsLoaded = true;
        return;
      }
      const countAbonents = this.tableAbonents.length;
      let countLoadedAbonents = 0;
      this.tableAbonents.forEach((abonent) => {
        const doorphoneData = this.getDoorphoneData(abonent);
        this.$set(abonent, 'full_address', doorphoneData[0]);
        this.$set(abonent, 'doorphone_router_link', doorphoneData[1]);
        this.$set(abonent, 'key_code', this.getKeyCode(abonent));
        this.$set(abonent, 'apartment_paid', this.getApartmentPaid(abonent));
        this.$set(abonent, 'status_text', this.getAbonentStatusText(abonent.status));
        this.$set(abonent, 'account_id', null);
        this.$set(abonent, 'money', null);
        this.$set(abonent, 'reckoning_date', null);
        this.$set(abonent, 'tp_name', null);
      });

      const loadBillingInfoForAbonents = () => {
        this.tableAbonents.forEach((abonent) => {
          const hasMainDoorphone = this.hasMainDoorphone(abonent);
          const hasSips = abonent.apartment_sip_settings && abonent.apartment_sip_settings.length > 0;
          if (hasMainDoorphone && hasSips) {
            this.billingInfo(abonent.apartment_sip_settings[0].id)
              .then((res) => {
                this.$set(abonent, 'account_id', res.account_info.account_id);
                this.$set(abonent, 'money', res.account_info.money);
                this.$set(abonent, 'reckoning_date', res.service_info[0].reckoning_date);
                this.$set(abonent, 'tp_name', res.service_info[0].tp_name);
              })
              .catch(() => {
                if (!this.billingInfoErrorShown) {
                  this.billingInfoErrorShown = true;
                }
              })
              .finally(() => {
                countLoadedAbonents += 1;
                if (countLoadedAbonents === countAbonents) {
                  this.abonentsLoaded = true;
                }
              });
          } else {
            countLoadedAbonents += 1;
            if (countLoadedAbonents === countAbonents) {
              this.abonentsLoaded = true;
            }
          }
        });
      };

      this.connectEvoApi()
        .catch(() => {
          this.$flash_warning('Не удалось подключиться к биллингу');
        })
        .finally(() => {
          loadBillingInfoForAbonents();
        });
    },
    async createMissingAccounts () {
      try {
        this.creatingAccounts = true;
        const res = await this.createBillingAccount(String(this.client.telephone));
        const data = res && res.data ? res.data : {};

        const errors = Array.isArray(data.errors) ? data.errors : [];

        if (!data.success) {
          this.$flash_warning('Не удалось создать лицевой счет');
          if (errors.length > 0) {
            this.accountCreationErrors = errors;
            this.errorsDialog = true;
          }
          return;
        }

        if (errors.length === 0) {
          this.reloadPage();
          return;
        }

        this.$flash_success('Лицевой счет создан');
        this.accountCreationErrors = errors;
        this.errorsDialog = true;
      } catch (e) {
        this.$flash_danger('Ошибка при создании лицевого счета');
        console.error(e);
      } finally {
        this.creatingAccounts = false;
      }
    },
    removeClient (id) {
      this.deleteClient(id)
        .then(() => {
          this.$flash_success('Клиент успешно удален');
          this.$router.push({ name: 'ListClients' });
        })
        .catch((err) => {
          this.$flash_danger('Не удалость удалить клиента');
          this.updateResult = err.response.data;
          console.error(err);
        })
        .finally(() => {
          this.removeDialog[id] = false;
        });
    },
    newAbonentClient (id) {
      this.$router.push({ name: 'NewAbonent', params: { id: id } });
    },
    removeAbonent (id) {
      this.deleteAbonent({
        id: id,
        comment: this.commentDelete,
      })
        .then(() => {
          this.$flash_success('Аккаунт успешно удален');
          this.getData();
        })
        .catch((message) => {
          if (message.length > 0) {
            for (let i = 0; i < message.length; i++) {
              this.$flash_warning(message[i]);
            }
          } else {
            this.$flash_warning('Ошибка при удалении аккаунта');
          }
        })
        .finally(() => {
          this.commentDelete = '';
          this.dialog[id] = false;
        });
    },
    hasMainDoorphone (abonent) {
      return Array.isArray(abonent.doorphones) && abonent.doorphones.some(d => d && d.type === 'M');
    },
    forbidCarNumberActivationAbonent (idAbonent) {
      this.forbidCarNumberActivation(idAbonent)
        .then(() => {
          this.getData();
          this.$flash_success('Активация ГРЗ в моб. приложении запрещена');
        })
        .catch((e) => {
          console.error(e);
          this.$flash_warning('Ошибка при запрете активации ГРЗ');
        });
    },
    allowCarNumberActivationAbonent (idAbonent) {
      this.allowCarNumberActivation(idAbonent)
        .then(() => {
          this.getData();
          this.$flash_success('Активация ГРЗ в моб. приложении разрешена');
        })
        .catch((e) => {
          console.error(e);
          this.$flash_warning('Ошибка при разрешении активации ГРЗ');
        });
    },
    getDoorphoneAddress (doorphone) {
      let address = doorphone.address;
      if (doorphone.type === 'G') {
        address += ' (калитка)';
      }
      if (doorphone.entrance > 0) {
        address = `${doorphone.address}, п.${doorphone.entrance}, кв.${doorphone.apartment}`;
        if (doorphone.type === 'A') {
          address += ' (доп.вход)';
        }
      }
      return address;
    },
    getDoorphoneData (abonent) {
      let address = abonent.address;
      let doorphoneRouterLink = '#';
      if (abonent.doorphones.length > 0) {
        const doorphone = abonent.doorphones[0];
        doorphoneRouterLink = '/doorphones/' + doorphone.id;
        if (doorphone.type === 'G') {
          address = `${doorphone.address},`;
          address += ' (калитка)';
        }
        if (doorphone.type === 'M') {
          address = `${doorphone.address}, п.${doorphone.entrance}, кв.${abonent.apartment}`;
        }
        if (doorphone.type === 'A') {
          address = `${doorphone.address}, п.${doorphone.entrance}, кв.${abonent.apartment}`;
          address += ' (доп.вход)';
        }
      }
      return [address, doorphoneRouterLink];
    },
    editClient (id) {
      this.$router.push({ name: 'EditClient', params: { id: id } });
    },
    getKeyCode (abonent) {
      if (abonent.keys.length > 0) {
        return abonent.keys[0].code;
      }
      return null;
    },
    getApartmentPaid (abonent) {
      if (abonent.apartment_sip_settings.length > 0) {
        return abonent.apartment_sip_settings[0].paid ? 'Pro - версия' : 'Lite - версия';
      }
      return null;
    },
    getAbonentStatusText (status) {
      return (status === 'A') ? 'Активен' : (status === 'B') ? 'Заблокирован' : 'Удалён';
    },
    getAbonentDoorphoneType (abonent) {
      if (!abonent || !Array.isArray(abonent.doorphones) || !abonent.doorphones.length) {
        return null;
      }
      return abonent.doorphones[0] && abonent.doorphones[0].type ? abonent.doorphones[0].type : null;
    },
    getAbonentDoorphoneForTab (abonent) {
      if (!abonent || !Array.isArray(abonent.doorphones) || !abonent.doorphones.length) {
        return null;
      }
      if (this.activeDoorphoneTab === 1) {
        return abonent.doorphones.find(doorphone => doorphone.type === 'G') || null;
      }
      if (this.activeDoorphoneTab === 2) {
        return abonent.doorphones.find(doorphone => doorphone.type === 'A') || null;
      }
      return abonent.doorphones.find(doorphone => doorphone.type !== 'G' && doorphone.type !== 'A') || abonent.doorphones[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.general-info-client {
  &__header {
    flex-wrap: wrap;
    gap: 8px;
  }
  &__title-block {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    padding-left: 0;
    margin-bottom: 0;
    .v-icon {
      vertical-align: middle;
    }
  }
  &__without-doorphone-note {
    font-size: 0.875rem;
    color: #e65100;
    margin: -2px 0 5px 32px;
    line-height: 1.4;
  }
  &__info {
    padding-top: 8px;
    padding-bottom: 16px;
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }
  &__footer {
    justify-content: flex-end;
  }
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 560px;

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    line-height: 1.45;
  }
  &__icon {
    flex-shrink: 0;
    margin-top: 2px;
  }
  &__label {
    flex: 0 0 140px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.875rem;
  }
  &__value {
    flex: 1;
    min-width: 0;
    font-size: 0.875rem;
    word-break: break-word;
  }

  @media (max-width: 600px) {
    max-width: none;

    &__row {
      flex-wrap: wrap;
      align-items: flex-start;
      gap: 4px 10px;
    }
    &__icon {
      order: 0;
      width: 20px;
    }
    &__label {
      order: 1;
      flex: 0 0 calc(100% - 30px);
      font-size: 0.8125rem;
    }
    &__value {
      order: 2;
      flex: 1 1 100%;
      min-width: 0;
      padding-left: 30px;
      font-size: 0.875rem;
    }
  }
}

.section-header {
  margin-bottom: 4px;
}
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  padding-left: 0;
  .v-icon {
    vertical-align: middle;
  }
}

.general-info-client__doorphone-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.general-info-client__doorphone-tabs ::v-deep .v-tabs-bar {
  background: #f5f7f8;
}

.general-info-client__doorphone-tabs ::v-deep .v-tab {
  text-transform: none;
  font-weight: 500;
}

.general-info-client__doorphone-tabs ::v-deep .v-tabs-bar__prev,
.general-info-client__doorphone-tabs ::v-deep .v-tabs-bar__next {
  flex: 0 0 auto;
  min-width: 40px;
}

.routerLink {
  text-decoration: none;
  color: #434850;
}
a:hover.routerLink {
  text-decoration: underline;
  color: #434850;
}
span.colorStatus {
  &A {
    color: green;
  }
  &B {
    color: #d5d50b;
  }
  &D {
    color: red;
  }
}
</style>

<style lang="scss">
.apartment-modal-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}
.apartment-modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  padding: 20px 24px 12px;
}
.apartment-modal-title-inner {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.apartment-modal-title-icon {
  flex-shrink: 0;
  vertical-align: middle;
}
.apartment-modal-text {
  padding: 12px 24px 16px;
  color: rgba(0, 0, 0, 0.7);
  font-size: 0.95rem;
}
.apartment-modal-actions {
  padding: 12px 24px 20px;
  gap: 8px;
}
.apartment-modal-actions .v-btn {
  text-transform: none;
  font-weight: 500;
}
</style>
